import { httpClient } from '../http-client'
import type {
  ProductsShowcaseParams,
  ProductsShowcaseResponse,
  ClientProductsParams,
  ClientProductsResponse,
  ProductClassesParams,
  ProductClassesResponse,
  ProductCategory,
  GlobalSearchParams,
  GlobalSearchResponse,
} from '@/types/api'

/**
 * ProductService - Gerencia produtos e turmas
 */
class ProductService {
  async getProductCategories(
    clientId: string,
    mentorId: string,
    options?: { skipCache?: boolean },
  ): Promise<ProductCategory[]> {
    const params = new URLSearchParams({ profileId: clientId })
    if (options?.skipCache) params.set('skipCache', 'true')

    const response = await httpClient.get(`/categories/${mentorId}/with-products?${params.toString()}`)
    const data = response.data

    if (!Array.isArray(data)) {
      throw new Error('Invalid categories response')
    }

    return data as ProductCategory[]
  }

  // Produtos
  async getProductsShowcase(params?: ProductsShowcaseParams): Promise<ProductsShowcaseResponse> {
    const response = await httpClient.get('/products/showcase', { params })
    return response.data
  }

  async getClientProducts(clientId: string, mentorId?: string): Promise<ClientProductsResponse> {
    const response = await httpClient.get(`/products/showcase?profileId=${clientId}${mentorId ? `&mentorId=${mentorId}` : ''}`)
    return response.data
  }

  async getProductById(productId: string) {
    const response = await httpClient.get(`/products/${productId}`)
    return response.data
  }

  async createProduct(data: any) {
    const response = await httpClient.post('/products', data)
    return response.data
  }

  async updateProduct(productId: string, data: any) {
    const response = await httpClient.patch(`/products/${productId}`, data)
    return response.data
  }

  async deleteProduct(productId: string) {
    const response = await httpClient.delete(`/products/${productId}`)
    return response.data
  }

  // Turmas
  async getProductClasses(productId: string, params?: ProductClassesParams): Promise<ProductClassesResponse> {
    const response = await httpClient.get(`/products/${productId}/classes`, { params })
    return response.data as ProductClassesResponse
  }

  async getClassById(classId: string) {
    const response = await httpClient.get(`/classes/${classId}`)
    return response.data
  }

  async createClass(productId: string, data: any) {
    const response = await httpClient.post(`/products/${productId}/classes`, data)
    return response.data
  }

  async updateClass(classId: string, data: any) {
    const response = await httpClient.patch(`/classes/${classId}`, data)
    return response.data
  }

  async deleteClass(classId: string) {
    const response = await httpClient.delete(`/classes/${classId}`)
    return response.data
  }

  // Inscrição em produto gratuito
  async enrollFreeProduct(userId: string, productId: string): Promise<{ success: boolean }> {
    const response = await httpClient.post('/clients/product/assign', {
      userId,
      productId,
      approved: true,
      origin: 'Vitrine',
    })
    return response.data
  }

  // Solicitar acesso a produto (cria relação com status pending)
  async requestProductAccess(userId: string, productId: string): Promise<{ success: boolean }> {
    const response = await httpClient.post('/clients/product/request-access', {
      userId,
      productId,
    })
    return response.data
  }

  // Global Search
  async globalSearch(params: GlobalSearchParams): Promise<GlobalSearchResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('searchTerm', params.searchTerm)

    if (params.mentorId) {
      queryParams.append('mentorId', params.mentorId)
    }

    if (params.userId) {
      queryParams.append('userId', params.userId)
    }

    if (params.mentorIds && params.mentorIds.length > 0) {
      queryParams.append('mentorIds', params.mentorIds.join(','))
    }

    const response = await httpClient.get(`/global-search?${queryParams.toString()}`)
    return response.data as GlobalSearchResponse
  }
}

export const productService = new ProductService()
